import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, CheckCircle2, Loader2, ArrowRight, Clock, Stethoscope, ShieldCheck, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Human-friendly SLA formatting.
 *
 * The backend returns `sla_hours` as a raw number. When a doctor configures a
 * long SLA (e.g. 336h for a 14-day derivation window) rendering it as "336
 * horas hábiles" is jarring and reads as an error. Rule:
 *   - ≤ 48h → keep hours ("48 horas hábiles" is idiomatic in AR healthcare)
 *   - > 48h → switch to business days, dividing by 24 (next-business-day
 *     convention, matches how AR clinics communicate SLAs)
 */
function formatSlaWait(hours) {
  const h = Number(hours) || 48;
  if (h <= 48) return `${h} horas hábiles`;
  const days = Math.round(h / 24);
  return `${days} días hábiles`;
}

const PaymentPage = () => {
  const { consultId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const [summary, setSummary] = useState(null);

  // Reuse artifacts persisted by IntakeForm.js right after submit.
  // Enables a discreet "Iniciar otra solicitud" CTA after payment confirmation.
  let reusePrefillToken = null;
  let lastSourceSlug = null;
  try {
    reusePrefillToken = sessionStorage.getItem('fleni:reuse_prefill_token');
    lastSourceSlug = sessionStorage.getItem('fleni:last_source_slug');
  } catch (e) { /* silent */ }
  const canStartAnother = !!(reusePrefillToken && lastSourceSlug);
  const startAnother = () => {
    navigate(`/${lastSourceSlug}?prefill_token=${encodeURIComponent(reusePrefillToken)}`);
  };

  const loadPaymentInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/consults/${consultId}/public-summary`);
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch {
      // Silent fail - the page still works without the doctor name
    }
  }, [consultId]);

  useEffect(() => {
    loadPaymentInfo();
  }, [loadPaymentInfo]);

  const handleContinue = async () => {
    setLoading(true);
    try {
      // Confirmar consulta sin pago real
      const response = await fetch(`${API_URL}/api/payments/${consultId}/simulate-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setPaid(true);
        toast.success("¡Consulta confirmada exitosamente!");
        // No auto-redirect - let user read the confirmation and click "Volver al Inicio"
      } else {
        toast.error("Error al procesar la consulta");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  // Private practice consults show the doctor's own price (Phase 1: informative,
  // the doctor coordinates the payment; no online payment yet).
  const isPrivate = !!summary?.is_private_practice && (summary?.consultation_price || 0) > 0;
  const privatePrice = summary?.consultation_price || 0;
  const logoSrc = summary?.is_private_practice ? "/logo-thales.png" : "/logo-fleni.png";
  const brandName = summary?.institution_name || (summary?.is_private_practice ? "Consultorio Privado" : "Fleni");

  if (paid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-xl border border-slate-200 p-12 shadow-sm">
            <div className="text-center mb-8">
              <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4">
                ¡Consulta enviada!
              </h1>
              <p className="text-slate-600">
                Tu consulta ha sido registrada correctamente
              </p>
            </div>

            <div className="space-y-6 mb-8">
              <div className="p-5 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-slate-700 leading-relaxed">
                  Tu consulta ya fue enviada y está en proceso de asignación a un profesional médico.
                </p>
              </div>

              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                <p className="text-sm text-slate-600">
                  <strong>ID de Consulta:</strong>
                </p>
                <code className="block bg-white px-3 py-2 rounded text-sm font-mono border">
                  {consultId}
                </code>
                <p className="text-xs text-slate-500 mt-2">
                  Guardá este ID para futuras referencias.
                </p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">¿Qué sigue ahora?</h3>
                <ol className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start">
                    <span className="font-bold mr-2 text-blue-600">1.</span>
                    Un profesional médico revisará tu consulta.
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold mr-2 text-blue-600">2.</span>
                    Recibirás un email cuando haya sido respondida.
                  </li>
                </ol>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Tiempo estimado de respuesta:</strong> {formatSlaWait(summary?.sla_hours)}.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/')}
                className="flex-1 h-12 bg-[#1d366b] hover:bg-[#16284f] text-white"
                data-testid="return-home-btn"
              >
                Volver al Inicio
              </Button>
              {canStartAnother && (
                <Button
                  onClick={startAnother}
                  variant="outline"
                  className="flex-1 h-12"
                  data-testid="payment-start-another-btn"
                >
                  Iniciar otra solicitud
                </Button>
              )}
            </div>

            <p className="text-center text-xs text-slate-500 mt-6">
              Gracias por confiar en {brandName}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img src={logoSrc} alt={brandName} className="h-8 sm:h-10 object-contain" />
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white rounded-xl border border-slate-200 p-8 md:p-12 shadow-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Tu consulta está lista
            </h1>
            <p className="text-slate-600">
              Confirmá los detalles para enviarla al profesional
            </p>
          </div>

          {/* Reframe block: VALUE BEFORE PRICE */}
          <div className="bg-white border-2 border-[#1d366b]/15 rounded-xl p-6 mb-6 shadow-sm" data-testid="value-reframe">
            <div className="flex items-start gap-4 mb-4">
              <div className="h-12 w-12 bg-[#1d366b]/8 rounded-full flex items-center justify-center flex-shrink-0">
                <Stethoscope className="h-6 w-6 text-[#1d366b]" />
              </div>
              <div className="flex-1">
                <p className="text-base text-slate-900 leading-relaxed">
                  Tu consulta va a ser atendida por{" "}
                  {summary?.doctor_name ? (
                    <strong className="text-[#1d366b]" data-testid="assigned-doctor-name">
                      {summary.doctor_name}
                      {summary.doctor_specialty ? ` · ${summary.doctor_specialty}` : ""}
                    </strong>
                  ) : (
                    <strong className="text-[#1d366b]">un profesional asignado</strong>
                  )}
                  .
                </p>
              </div>
            </div>
            <div className="space-y-2.5 pl-1">
              <div className="flex items-start gap-2.5 text-sm text-slate-700">
                <FileText className="h-4 w-4 text-[#1d366b] mt-0.5 flex-shrink-0" />
                <span>Incluye análisis y respuesta en los tiempos acordados.</span>
              </div>
              <div className="flex items-start gap-2.5 text-sm text-slate-700">
                <ShieldCheck className="h-4 w-4 text-[#1d366b] mt-0.5 flex-shrink-0" />
                <span>Queda registrada en tu historia clínica digital.</span>
              </div>
              <div className="flex items-start gap-2.5 text-sm text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-[#1d366b] mt-0.5 flex-shrink-0" />
                <span><strong>Es un acto médico formal</strong>, con la misma validez que una consulta presencial.</span>
              </div>
            </div>
          </div>

          {/* Free trial banner (institutional) */}
          {!isPrivate && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-3" data-testid="free-trial-banner">
              <Gift className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              <p className="text-sm text-emerald-900">
                <strong>Período de prueba:</strong> esta consulta es sin costo para vos.
              </p>
            </div>
          )}

          {/* Precio */}
          {/*
            PILOTO — Consultorio Privado: hasta que integremos Mercado Pago
            formalmente, mostramos el mismo bloque "Sin cargo" que usamos en
            Fleni. El precio real del profesional aparece tachado al lado del
            "$0" para que el paciente entienda el valor que está recibiendo
            gratis durante el piloto. Cuando MP esté OAuth-conectado, este
            bloque vuelve al desglose "Honorario + Cargo de servicio + Total".
          */}
          {isPrivate ? (
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 mb-8 border border-emerald-200" data-testid="private-price-block">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Costo de esta consulta</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-emerald-600" data-testid="private-price-value">$0</span>
                    {privatePrice > 0 && (
                      <span className="text-slate-400 line-through text-lg" data-testid="private-price-original">
                        ${privatePrice.toLocaleString('es-AR')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {privatePrice > 0
                      ? `Precio regular del profesional: $${privatePrice.toLocaleString('es-AR')}`
                      : "Sin costo durante el período piloto"}
                  </p>
                </div>
                <div className="bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  SIN CARGO
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 mb-8 border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Costo de esta consulta</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-emerald-600">$0</span>
                    <span className="text-slate-400 line-through text-lg">$24.200</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Precio regular: $20.000 + IVA</p>
                </div>
                <div className="bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  GRATIS
                </div>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="space-y-4 mb-8">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              {isPrivate ? (
                <p className="text-slate-700 leading-relaxed" data-testid="private-payment-note">
                  Durante el <strong>período piloto</strong> podés enviar tu consulta <strong>sin costo</strong>.
                  Cuando habilitemos el cobro online, tu profesional te avisará el precio antes de confirmar.
                </p>
              ) : (
                <p className="text-slate-700 leading-relaxed">
                  Durante el período de prueba, podés utilizar el servicio de consultas médicas 
                  asincrónicas <strong>sin ningún costo</strong>. 
                </p>
              )}
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-3">Al continuar:</h3>
              
              {/* SLA destacado */}
              <div className="mb-3 p-3 bg-white border-2 border-blue-300 rounded-lg flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-base font-semibold text-slate-900">Tiempo estimado de respuesta</p>
                  <p className="text-lg font-bold text-blue-700" data-testid="payment-sla-hours">
                    {formatSlaWait(summary?.sla_hours)}
                  </p>
                </div>
              </div>

              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>El médico puede, según su criterio profesional, derivar tu consulta a una atención presencial o teleconsulta si lo considera necesario.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  Tu consulta será asignada a un profesional médico.
                </li>
              </ul>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">
                <strong>ID de Consulta:</strong>
              </p>
              <code className="block bg-white px-2 py-1 rounded border text-xs break-all">{consultId}</code>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleContinue}
            disabled={loading}
            className="w-full h-14 bg-[#1d366b] hover:bg-[#16284f] text-white text-lg font-medium"
            data-testid="continue-free-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                Continuar sin cargo
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
